/**
 * PII Masking Utilities for Analytics
 * Ensures sensitive data is never sent to PostHog
 */

/** Regex patterns for detecting sensitive data */
const PII_PATTERNS = {
  /** Credit/debit card numbers (13-19 digits, possibly with spaces/dashes) */
  cardNumber: /\b(?:\d[ -]*?){13,19}\b/g,
  /** Account numbers (8-20 digits) */
  accountNumber: /\b\d{8,20}\b/g,
  /** Email addresses */
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  /** Phone numbers (various formats) */
  phone: /(?:\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
  /** SSN/TIN (US format) */
  ssn: /\b\d{3}[-]?\d{2}[-]?\d{4}\b/g,
  /** UUID patterns (often used as user/transaction IDs) */
  uuid: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
  /** Monetary amounts with currency symbols */
  amount: /[$€£¥]\s*[\d,]+\.?\d*/g,
  /** JWT tokens */
  jwt: /eyJ[a-zA-Z0-9_-]*\.eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g,
  /** IP addresses */
  ipAddress: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g
} as const;

/** Keys that should always be excluded from properties */
const SENSITIVE_KEYS = new Set([
  'password',
  'token',
  'secret',
  'api_key',
  'apiKey',
  'authorization',
  'auth',
  'credential',
  'ssn',
  'social_security',
  'card_number',
  'cardNumber',
  'cvv',
  'cvc',
  'pin',
  'account_number',
  'accountNumber',
  'routing_number',
  'routingNumber',
  'email',
  'phone',
  'address',
  'name',
  'first_name',
  'firstName',
  'last_name',
  'lastName',
  'full_name',
  'fullName',
  'dob',
  'date_of_birth',
  'dateOfBirth',
  'birth_date',
  'birthDate'
]);

/** URL parameters that should be masked */
const SENSITIVE_URL_PARAMS = new Set([
  'id',
  'userId',
  'user_id',
  'transactionId',
  'transaction_id',
  'accountId',
  'account_id',
  'cardId',
  'card_id',
  'token',
  'code',
  'key'
]);

/**
 * Mask a string value by replacing sensitive patterns
 * @param value - The string to mask
 * @returns Masked string with PII replaced
 */
export function maskString(value: string): string {
  if (typeof value !== 'string') return value;

  let masked = value;

  // Apply all PII pattern masks
  masked = masked.replace(PII_PATTERNS.cardNumber, '[CARD_MASKED]');
  masked = masked.replace(PII_PATTERNS.email, '[EMAIL_MASKED]');
  masked = masked.replace(PII_PATTERNS.phone, '[PHONE_MASKED]');
  masked = masked.replace(PII_PATTERNS.ssn, '[SSN_MASKED]');
  masked = masked.replace(PII_PATTERNS.jwt, '[TOKEN_MASKED]');
  masked = masked.replace(PII_PATTERNS.ipAddress, '[IP_MASKED]');
  // Only mask long number sequences that look like account numbers
  masked = masked.replace(/\b\d{10,20}\b/g, '[ACCOUNT_MASKED]');

  return masked;
}

/**
 * Sanitize a URL by masking sensitive path segments and query parameters
 * @param url - The URL to sanitize
 * @returns Sanitized URL
 */
export function sanitizeUrl(url: string): string {
  if (!url || typeof url !== 'string') return url;

  try {
    // Handle relative URLs
    const isRelative = url.startsWith('/');
    const fullUrl = isRelative ? `https://placeholder.com${url}` : url;
    const parsed = new URL(fullUrl);

    // Mask sensitive query parameters
    for (const param of SENSITIVE_URL_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[MASKED]');
      }
    }

    // Mask UUID-like path segments
    let pathname = parsed.pathname;
    pathname = pathname.replace(PII_PATTERNS.uuid, '[ID_MASKED]');
    // Mask numeric IDs in paths (e.g., /users/12345 -> /users/[ID])
    pathname = pathname.replace(/\/\d{4,}/g, '/[ID]');

    const result = isRelative
      ? `${pathname}${parsed.search}`
      : `${parsed.origin}${pathname}${parsed.search}`;

    return result;
  } catch {
    // If URL parsing fails, do basic string masking
    return maskString(url);
  }
}

/**
 * Check if a key is sensitive and should be excluded
 * @param key - Property key to check
 * @returns Whether the key is sensitive
 */
export function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return (
    SENSITIVE_KEYS.has(key) ||
    SENSITIVE_KEYS.has(lowerKey) ||
    lowerKey.includes('password') ||
    lowerKey.includes('secret') ||
    lowerKey.includes('token') ||
    lowerKey.includes('credential')
  );
}

/**
 * Recursively sanitize an object's properties
 * @param obj - Object to sanitize
 * @param depth - Current recursion depth (max 5)
 * @returns Sanitized object
 */
export function sanitizeProperties(
  obj: Record<string, unknown>,
  depth = 0
): Record<string, unknown> {
  if (depth > 5 || !obj || typeof obj !== 'object') {
    return obj;
  }

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Skip sensitive keys entirely
    if (isSensitiveKey(key)) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Handle different value types
    if (value === null || value === undefined) {
      sanitized[key] = value;
    } else if (typeof value === 'string') {
      // Special handling for URL-like keys
      if (key.toLowerCase().includes('url') || key.toLowerCase().includes('path')) {
        sanitized[key] = sanitizeUrl(value);
      } else {
        sanitized[key] = maskString(value);
      }
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? sanitizeProperties(item as Record<string, unknown>, depth + 1)
          : typeof item === 'string'
            ? maskString(item)
            : item
      );
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeProperties(value as Record<string, unknown>, depth + 1);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Create a PostHog-compatible sanitize_properties function
 * This is used as the sanitize_properties option in PostHog config
 */
export function createPostHogSanitizer() {
  return (
    properties: Record<string, unknown>,
    _event: string
  ): Record<string, unknown> => {
    const sanitized = sanitizeProperties(properties);

    // Additional sanitization for PostHog-specific properties
    if (sanitized.$current_url && typeof sanitized.$current_url === 'string') {
      sanitized.$current_url = sanitizeUrl(sanitized.$current_url);
    }
    if (sanitized.$pathname && typeof sanitized.$pathname === 'string') {
      sanitized.$pathname = sanitizeUrl(sanitized.$pathname);
    }
    if (sanitized.$referrer && typeof sanitized.$referrer === 'string') {
      sanitized.$referrer = sanitizeUrl(sanitized.$referrer);
    }

    return sanitized;
  };
}

/** CSS class to add to elements that should be masked in session recordings */
export const MASK_CSS_CLASS = 'ph-no-capture';

/** Data attribute to mark elements for masking */
export const MASK_DATA_ATTR = 'data-ph-capture-attribute-masked';

/**
 * CSS selector for elements that should be masked in session recordings
 * Add this to PostHog's maskTextSelector config
 */
export const MASK_TEXT_SELECTOR = [
  '[data-mask]',
  '.mask-pii',
  '.ant-input',
  '.ant-input-password',
  '.ant-select-selection-item',
  '.ant-picker-input input',
  '[data-testid*="amount"]',
  '[data-testid*="account"]',
  '[data-testid*="card"]',
  '[data-testid*="email"]',
  '[data-testid*="phone"]',
  '[data-testid*="name"]',
  '[data-testid*="address"]'
].join(', ');
